import { contentGet } from '../email-activity/content-api'

// Content's organization search (owner 2026-10-13): the one way the admin
// reads organizations for a picker, a grid or a name. Never the whole list
// (`api/ddd/organization`, about 9,500 on prod, almost all personal): loading
// it froze every page that did.
//
// GET organization/search: q (name contains), type (all | company |
// personal), limit (1-100, default 20), cursor (the previous page's next),
// ids (up to 100: those organizations only). Answers { data: [{ id, name,
// personal, createdAt }], next } -- next is null on the last page. Live
// organizations only, company first, then by name.

export const MAX_LIMIT = 100

// Names this page has seen: id -> { id, name, personal }, or null for an id
// that names no live organization. Filled by every search and lookup.
const known = new Map()

const remember = rows => (rows || []).forEach(o => known.set(Number(o.id), { id: Number(o.id), name: o.name, personal: Boolean(o.personal) }))

// For tests.
export const forgetOrganizations = () => known.clear()

export const organizationNameOf = id => {
  const o = known.get(Number(id))
  return o ? o.name : undefined
}

const compact = params => Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))

export const searchOrganizations = async ({ q, type, limit, cursor } = {}) => {
  const page = (await contentGet('organization/search', compact({ q: (q || '').trim(), type, limit, cursor }))) || {}
  const data = page.data || []
  remember(data)
  return { data, next: page.next || null }
}

const validID = v => {
  const n = Number(v)
  return Number.isInteger(n) && n > 0 ? n : null
}

// The organizations these ids name, as a Map id -> { id, name, personal }.
// Asks content only for ids it hasn't seen, 100 at a time; an id that names
// no live organization is left out (and not asked about again).
export const lookupOrganizations = async ids => {
  const wanted = [...new Set((ids || []).map(validID).filter(Boolean))]
  const missing = wanted.filter(id => !known.has(id))
  for (let i = 0; i < missing.length; i += MAX_LIMIT) {
    const chunk = missing.slice(i, i + MAX_LIMIT)
    const page = (await contentGet('organization/search', { ids: chunk.join(',') })) || {}
    remember(page.data)
    chunk.forEach(id => {
      if (!known.has(id)) known.set(id, null)
    })
  }
  const out = new Map()
  wanted.forEach(id => {
    const o = known.get(id)
    if (o) out.set(id, o)
  })
  return out
}

// "12" or "#12": someone typing an organization's id.
export const idQuery = text => {
  const m = /^#?(\d{1,10})$/.exec((text || '').trim())
  return m ? validID(m[1]) : null
}

// A TableAgInfinite datasource over the search. The row model asks by offset
// (start, end); content pages by cursor. Each block's cursor is the next of
// the block before it, recorded as blocks arrive (per search text); the row
// model only asks for the block after one it already has, so a cursor is
// always known. The count stays open (-1) until the last page.
export const makeOrganizationGetRows = ({ type } = {}) => {
  const cursors = new Map()
  const key = (search, start) => `${search}\u0000${start}`

  return async (start, end, search) => {
    const q = (search || '').trim()
    const limit = Math.min(end - start, MAX_LIMIT)
    const cursor = start === 0 ? undefined : cursors.get(key(q, start))
    if (start > 0 && cursor === undefined) return { data: [], count: start }

    const page = await searchOrganizations({ q, type, limit, cursor })
    if (page.next) cursors.set(key(q, start + page.data.length), page.next)
    return { data: page.data, count: page.next ? -1 : start + page.data.length }
  }
}
